import { useRef } from 'react';
import { ChevronsUpDownIcon, CircleQuestionIcon } from '../../Icons';
import { headCellVariants } from './logic';
import styles from './styles.module.scss';

type HeadType = 'text' | 'none';

/**
 * ヘッダーに添えるヘルプの内容・表示制御ハンドラーをまとめたオブジェクト。
 *
 * TableHeadCell はヘルプの表示手段（ツールチップ等）を知らず、呼び出し側から
 * 渡された showHelp / hideHelp を呼ぶだけに徹する。
 */
type HelpContent = {
  /** ヘルプの本文 */
  content: React.ReactNode;
  /** ヘルプを表示するハンドラー（アンカー要素と本文を渡す） */
  showHelp: (anchorEl: HTMLElement, content: React.ReactNode) => void;
  /** ヘルプを非表示にするハンドラー */
  hideHelp: () => void;
  /** ヘルプの id（aria-describedby に使用） */
  helpId?: string;
};

type BaseProps = {
  /** ヘッダーの表示タイプ。デフォルト: 'text' */
  type?: HeadType;
  /** テキスト（type='text' の場合に表示） */
  label?: string;
  /** テキストを右寄せにする。デフォルト: false */
  rightTextAlign?: boolean;
  /** 追加のクラス名 */
  className?: string;
  /** ヘッダーに添えるヘルプ。指定時、label の隣にヘルプアイコンを描画する（sortable / 非 sortable いずれでも指定可） */
  helpContent?: HelpContent;
};

type SortableProps =
  | {
      sortable: true;
      onSort: () => void;
    }
  | {
      sortable?: false;
      onSort?: never;
    };

type Props = BaseProps & SortableProps;

export const TableHeadCell: React.FC<Props> = ({
  type = 'text',
  label,
  helpContent,
  rightTextAlign = false,
  sortable = false,
  onSort,
  className,
}) => {
  const align = rightTextAlign ? 'right' : 'left';
  const helpIconRef = useRef<HTMLSpanElement>(null);

  const showHelp = () => {
    if (!helpContent || !helpIconRef.current) return;
    helpContent.showHelp(helpIconRef.current, helpContent.content);
  };

  const hideHelp = () => {
    helpContent?.hideHelp();
  };

  if (type === 'none') {
    return <th className={[headCellVariants({ align }), className].filter(Boolean).join(' ')} />;
  }

  const helpIcon = helpContent && (
    // biome-ignore lint/a11y/noStaticElementInteractions: この span は意図的に button ではない（sortable の場合は sort button の中に button をネストできないため）。キーボード・スクリーンリーダーからのアクセスは aria-describedby + フォーカスで担保し、この span はマウス・タッチのホバー・クリックのみを扱う
    <span
      ref={helpIconRef}
      className={styles.helpIcon}
      onClick={event => event.stopPropagation()}
      onMouseEnter={showHelp}
      onMouseLeave={hideHelp}
    >
      <CircleQuestionIcon size='xs' aria-hidden />
    </span>
  );

  return (
    <th
      className={[headCellVariants({ align }), className].filter(Boolean).join(' ')}
      aria-describedby={!sortable && helpContent ? helpContent.helpId : undefined}
    >
      {sortable ? (
        <button
          type='button'
          className={styles.sortableButton}
          onClick={onSort}
          aria-describedby={helpContent?.helpId}
          onFocus={showHelp}
          onBlur={hideHelp}
        >
          {label}
          {helpIcon}
          <ChevronsUpDownIcon size='xs' className={styles.sortIcon} />
        </button>
      ) : (
        <>
          {label}
          {helpIcon}
        </>
      )}
    </th>
  );
};
